import { Routes, Route, Navigate } from 'react-router-dom'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setUser, setLoading } from './store/slices/authSlice'
import { apiService } from './services/api'
import type { RootState } from './store'

// Layouts
import DashboardLayout from './components/layout/DashboardLayout'

// Pages
import Home from './pages/Home'
import Login from './pages/Login'
import Register from './pages/Register'
import Dashboard from './pages/Dashboard'
import FormManagement from './pages/FormManagement'
import FormEditor from './pages/FormEditor'
import FormSubmissions from './pages/FormSubmissions'
import OrderManagement from './pages/OrderManagement'
import ProductManagement from './pages/ProductManagement'
import UserManagement from './pages/UserManagement'
import SystemSettings from './pages/SystemSettings'

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useSelector((state: RootState) => state.auth)

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

// Public Route Component - 已登录用户重定向到Dashboard
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useSelector((state: RootState) => state.auth)

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

function App() {
  const dispatch = useDispatch()

  useEffect(() => {
    // 检查本地存储的 token
    const token = localStorage.getItem('token')
    if (token) {
      apiService.setToken(token)
      // 获取用户信息
      apiService.getProfile().then((response) => {
        if (response.data?.user) {
          dispatch(setUser(response.data.user))
        }
        dispatch(setLoading(false))
      }).catch(() => {
        dispatch(setLoading(false))
      })
    } else {
      dispatch(setLoading(false))
    }
  }, [dispatch])

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Home />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="forms" element={<FormManagement />} />
        <Route path="forms/new" element={<FormEditor />} />
        <Route path="forms/:id/edit" element={<FormEditor />} />
        <Route path="forms/:id/submissions" element={<FormSubmissions />} />
        <Route path="orders" element={<OrderManagement />} />
        <Route path="products" element={<ProductManagement />} />
        <Route path="users" element={<UserManagement />} />
        <Route path="settings" element={<SystemSettings />} />
        <Route path="settings/profile" element={<SystemSettings />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default App
